// client/src/components/AdminPrompts.js
// Prompts management component for superadmins

import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { HeadlineLarge, BodyMedium, LabelMedium } from './Typography';
import Toast from './Toast';

const AdminPrompts = ({ onNavigateToPrompt }) => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [lengthFilter, setLengthFilter] = useState('');

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Toast notification state
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  // Load all prompts
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Load prompts
        const promptsRef = collection(db, 'prompts');
        const promptsQuery = query(promptsRef, orderBy('title', 'asc'));
        const promptsSnapshot = await getDocs(promptsQuery);
        
        const promptsData = [];
        promptsSnapshot.forEach(doc => {
          promptsData.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort by title
        promptsData.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        
        setPrompts(promptsData);
      } catch (error) {
        console.error('Error loading prompts data:', error);
        setError('Failed to load prompts. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter prompts based on search criteria
  const filteredPrompts = useMemo(() => {
    return prompts.filter(prompt => {
      // Search term filter (prompt title or body)
      const searchMatch = !searchTerm || 
        (prompt.title && prompt.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (prompt.body && prompt.body.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (prompt.description && prompt.description.toLowerCase().includes(searchTerm.toLowerCase()));

      // Category filter
      const categoryMatch = !categoryFilter || prompt.category === categoryFilter;

      // Length filter
      const lengthMatch = !lengthFilter || prompt.length === lengthFilter;

      return searchMatch && categoryMatch && lengthMatch;
    });
  }, [prompts, searchTerm, categoryFilter, lengthFilter]);

  const handleAddPrompt = () => {
    // Navigate to blank prompt edit page for new prompt
    onNavigateToPrompt(null);
  };

  const handleEditPrompt = (promptId) => {
    // Navigate to prompt edit page for existing prompt
    onNavigateToPrompt(promptId);
  };

  const handleDeleteClick = (prompt) => {
    setPromptToDelete(prompt);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!promptToDelete) return;

    try {
      setDeleting(true);
      
      // Delete the prompt from Firestore
      const promptRef = doc(db, 'prompts', promptToDelete.id);
      await deleteDoc(promptRef);
      
      // Remove from local state
      setPrompts(prompts.filter(p => p.id !== promptToDelete.id));
      
      // Show success message
      showToast('Prompt deleted successfully', 'success');
      
      // Close modal and reset state
      setShowDeleteConfirm(false);
      setPromptToDelete(null);
      
    } catch (error) {
      console.error('Error deleting prompt:', error);
      showToast('Failed to delete prompt', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPromptToDelete(null);
  };

  const showToast = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setLengthFilter('');
  };

  const hasActiveFilters = searchTerm || categoryFilter || lengthFilter;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading prompts...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={hideToast}
        duration={3000}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && promptToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Prompt?</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "<strong>{promptToDelete.title}</strong>"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center">
        <HeadlineLarge color="gray-900">Prompts</HeadlineLarge>
        <button
          onClick={handleAddPrompt}
          className="px-4 py-2 bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200 transition-colors"
        >
          Add Prompt
        </button>
      </div>

      {/* Search and Filter Controls */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Search</LabelMedium>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Title or description..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Category Filter */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Category</LabelMedium>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              <option value="Revealing Characters">Revealing Characters</option>
              <option value="Literary Devices">Literary Devices</option>
              <option value="Showing vs. Telling">Showing vs. Telling</option>
              <option value="Setting">Setting</option>
              <option value="Scene Construction">Scene Construction</option>
              <option value="Dialogue">Dialogue</option>
              <option value="Hooks">Hooks</option>
              <option value="Scene Endings">Scene Endings</option>
            </select>
          </div>

          {/* Length Filter */}
          <div>
            <LabelMedium color="gray-700" className="block mb-2">Length</LabelMedium>
            <select
              value={lengthFilter}
              onChange={(e) => setLengthFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Lengths</option>
              <option value="5 minutes">5 minutes</option>
              <option value="10 minutes">10 minutes</option>
              <option value="20 minutes">20 minutes</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          <div className="flex items-end">
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {hasActiveFilters && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-blue-800">
              Showing {filteredPrompts.length} of {prompts.length} prompts
              {searchTerm && ` matching "${searchTerm}"`}
              {categoryFilter && ` in ${categoryFilter}`}
              {lengthFilter && ` (${lengthFilter})`}
            </div>
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Clear filters
            </button>
          </div>
        </div>
      )}

      {/* Prompts Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Prompt Name
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Length
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredPrompts.length > 0 ? (
              filteredPrompts.map((prompt) => (
                <tr key={prompt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <BodyMedium color="gray-900">{prompt.title || 'Untitled Prompt'}</BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">
                      {prompt.category || '-'}
                    </BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <BodyMedium color="gray-600">
                      {prompt.length || '-'}
                    </BodyMedium>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-3">
                      <button
                        onClick={() => handleEditPrompt(prompt.id)}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        edit
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        onClick={() => handleDeleteClick(prompt)}
                        className="text-red-600 hover:text-red-800 underline"
                      >
                        delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="px-6 py-12 text-center">
                  <div className="text-gray-500">
                    {hasActiveFilters ? (
                      <>
                        <p className="text-lg font-medium mb-2">No prompts match your search</p>
                        <p className="text-sm mb-4">Try adjusting your search criteria or clearing the search</p>
                        <button
                          onClick={clearFilters}
                          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Clear Search
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-medium mb-2">No prompts found</p>
                        <p className="text-sm">Get started by creating your first prompt</p>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-500 text-center">
        Showing {filteredPrompts.length} prompt{filteredPrompts.length !== 1 ? 's' : ''}
        {hasActiveFilters && ` of ${prompts.length} total`}
      </div>
    </div>
  );
};

export default AdminPrompts;
